import React from 'react';
import { useNavigate } from 'react-router-dom';
import { YButton } from '../../../ui/YButton';
import NavigationService from '../../../services/navigationService';
import { FaCrown, FaUsers } from 'react-icons/fa';
import { CachedImage } from '../../../components/common/CachedImage';

function initialsFrom(name, fallback = '?') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return fallback;
}

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function periodLabel(raw) {
  if (!raw) return '';
  return String(raw).replace(/_/g, ' ');
}

/**
 * Field-edition buddy card (Community 5c enamel).
 * Layout matches prototype: head (avatar · name · chip) + body (line · clubs · PROFILE/MESSAGE).
 */
export function BuddyProfileCard({ buddy, onMessage }) {
  const navigate = useNavigate();
  const data = buddy.profile || buddy || {};

  const isCurrentlyHunting = Boolean(data?.current_sign_out);
  const activeSit = data?.current_sign_out;
  const lastHunt = data?.last_sign_out;
  const clubs = data?.clubs_summary || [];
  const clubRoles = data?.club_roles || [];
  const method = data?.preferred_hunting_method;

  const displayName = buddy.first_name || buddy.username || 'Buddy';
  const rawHandle = data?.handle || buddy?.username || 'user';
  const handle = rawHandle.startsWith('@') ? rawHandle : `@${rawHandle}`;
  const init = initialsFrom(
    `${buddy.first_name || ''} ${buddy.last_name || ''}`.trim() || buddy.username,
  );

  const chip = isCurrentlyHunting
    ? 'IN THE WOODS'
    : (method ? String(method).toUpperCase() : 'BUDDY');

  let line = 'No recent sits logged yet.';
  if (isCurrentlyHunting && activeSit) {
    const period = periodLabel(activeSit.time_period) || 'hunt';
    const where = activeSit.stand_name || activeSit.location_label || activeSit.club_name;
    line = where
      ? `In the woods now — ${period} sit at ${where}.`
      : `In the woods now — ${period} sit.`;
  } else if (lastHunt) {
    const when = formatRelative(lastHunt.started_at || lastHunt.ended_at);
    const period = periodLabel(lastHunt.time_period);
    const where = lastHunt.stand_name || lastHunt.location_label || lastHunt.club_name;
    const bits = [`Last out ${when}`];
    if (period) bits.push(period);
    if (where) bits.push(`at ${where}`);
    line = `${bits.join(' · ')}.`;
  }

  const handleViewProfile = (e) => {
    e?.stopPropagation();
    navigate(`/users/${buddy.id}`);
  };

  const handleMessage = (e) => {
    e?.stopPropagation();
    if (onMessage) {
      onMessage(buddy);
    } else if (buddy?.id) {
      NavigationService.navigateToUserMessage(
        buddy.id,
        buddy.username || buddy.first_name || '',
        navigate,
      );
    }
  };

  return (
    <div
      className={`y-buddy-card y-buddy-card--field${isCurrentlyHunting ? ' is-hunting' : ''}`}
      data-testid="buddy-profile-card"
      onClick={handleViewProfile}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleViewProfile(e);
        }
      }}
    >
      <div className="y-buddy-card__head">
        <span className="y-buddy-card__avatar-wrap">
          {data?.photo_url ? (
            <CachedImage
              src={data.photo_url}
              alt={handle}
              className="y-buddy-card__avatar"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="y-buddy-card__avatar y-buddy-card__avatar--placeholder" aria-hidden="true">
              {init}
            </span>
          )}
          {isCurrentlyHunting ? (
            <span className="y-buddy-card__dot" title="Currently hunting" />
          ) : null}
        </span>
        <span className="y-buddy-card__identity">
          <span className="y-buddy-card__name">{displayName}</span>
          <span className="y-buddy-card__handle">{handle}</span>
        </span>
        <span className={`y-buddy-card__chip${isCurrentlyHunting ? ' is-hunting' : ''}`}>
          {chip}
        </span>
      </div>
      <div className="y-buddy-card__body">
        <p className="y-buddy-card__line">{line}</p>
        {clubs.length > 0 ? (
          <div className="y-buddy-card__clubs">
            {clubs.slice(0, 3).map((club, idx) => {
              const role = clubRoles.find((r) => r.club_name === club || r.club === club);
              return (
                <span key={`${club}-${idx}`} className="y-buddy-card__club">
                  {role?.role === 'president' ? <FaCrown size={9} aria-hidden="true" /> : null}
                  {role?.role === 'board_member' ? <FaUsers size={9} aria-hidden="true" /> : null}
                  {club}
                </span>
              );
            })}
            {clubs.length > 3 ? (
              <span className="y-buddy-card__club y-buddy-card__club--more">
                +
                {clubs.length - 3}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="y-buddy-card__actions">
          <YButton size="sm" variant="secondary" className="y-buddy-card__btn" onClick={handleViewProfile}>
            PROFILE
          </YButton>
          <YButton size="sm" variant="primary" className="y-buddy-card__btn y-buddy-card__btn--msg" onClick={handleMessage}>
            MESSAGE
          </YButton>
        </div>
      </div>
    </div>
  );
}
